import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { MODAL_SIZES } from '@hotjar/webapp/components/GenericModal';
import { GenericMessageModal } from '@hotjar/webapp/components/GenericModal/GenericMessageModal';
import { deleteSite, useSites } from '@hotjar/site';
import { useMutation } from 'react-query';
import { useNotify } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { SiteModel } from '../types';
import { useModalEvents } from '../../useModalEvents';
import { Event, UiPosition } from '../../events';

interface DeleteSiteModalProps {
  site: SiteModel;
  isOpen: boolean;
  onSuccess(): void;
  onCancel(): void;
}

const TRANSLATION_DOMAIN = 'account:delete-site';

export const DeleteSiteModal = ({
  site,
  isOpen,
  onSuccess,
  onCancel,
}: DeleteSiteModalProps): ReactElement => {
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);
  const { invalidate } = useSites();
  const { showToast } = useNotify();
  const { trackClicked, trackOpened } = useModalEvents({
    organizationId: site.organizationId,
    accountId: site.account.id as number,
    siteId: site.id,
    confirm: Event.CONFIRM_DELETE_SITE_MODAL,
    uiPosition: UiPosition.DELETE_SITE_MODAL,
    opened: Event.VIEW_DELETE_SITE_MODAL,
  });

  const mutation = useMutation(() => deleteSite(site.account.id as number, site.id), {
    onSuccess: () => {
      showToast({
        text: t('notify.success'),
        kind: 'info',
      });
      invalidate();
      onSuccess();
    },
    onError: () => {
      showToast({ text: t('notify.failure'), kind: 'danger' });
    },
  });

  useEffect(() => {
    trackOpened(isOpen);
  }, [isOpen, trackOpened]);

  return (
    <GenericMessageModal
      size={MODAL_SIZES.xSmall}
      title={t('modal.title')}
      description={t('modal.description', { site: site.name })}
      isOpen={isOpen}
      isLoading={mutation.isLoading}
      cancel={{ text: t('modal.cancel'), onClick: onCancel }}
      submit={{
        text: t('modal.submit'),
        kind: 'danger',
        onClick: () => {
          trackClicked();
          mutation.mutate();
        },
      }}
    />
  );
};
